import * as React from 'react';
import styled from 'styled-components';
import { MapMouseEvent, useMap } from 'react-map-gl/mapbox';

import { NumberFormatter } from '@longline/aqua-ui/formatters/NumberFormatter';
import { Dockable } from '@longline/aqua-ui/containers/Dock';
import { InfoBox } from '@longline/aqua-ui/containers/InfoBox';
import { LongitudeFormatter } from '@longline/aqua-ui/formatters/GIS/LongitudeFormatter';
import { LatitudeFormatter } from '@longline/aqua-ui/formatters/GIS/LatitudeFormatter';

import { useGlobalState } from '../contexts/state/GlobalStateContext';
import { IDW } from '../util/IDW';
import { ELayerType } from '../types/ELayerType';
import { LayerUtils } from '../util/LayerUtils';
import { ILayer } from '../types/ILayer';
import { LandDetector } from '../services/LandDetector';

interface ILayerValue {
  layer: ILayer;
  name: string;
  unit: React.ReactNode;
  decimals: number;
  value: number | number[] | null;
}

interface IProps {
  onCancel: () => void;
}

const DEBOUNCE_MS = 200;

const Eyedropper = (props: IProps) => {
  const map = useMap().current!;
  const globalState = useGlobalState();

  // Mouse position in lat/lng coordinates
  const [lngLat, setLngLat] = React.useState<{ lng: number; lat: number } | null>(null);
  // Calculated layer values
  const [layerValues, setLayerValues] = React.useState<ILayerValue[]>([]);
  // Whether the current position is on land
  const [isLand, setIsLand] = React.useState<boolean>(false);
  // Whether we're currently calculating (loading)
  const [isCalculating, setIsCalculating] = React.useState<boolean>(false);
  // Debounce timer ref
  const debounceRef = React.useRef<number | null>(null);
  // Last lng/lat for calculations (used by debounced function)
  const lngLatRef = React.useRef<{ lng: number; lat: number } | null>(null);

  const calculateValues = async () => {
    if (!lngLatRef.current) {
      setLayerValues([]);
      setIsLand(false);
      return;
    }

    const { lng, lat } = lngLatRef.current;

    setIsCalculating(true);

    // First check if we're on land
    const landDetector = LandDetector.getInstance();
    const onLand = await landDetector.isLand(lng, lat, globalState.project.clipping);
    setIsLand(onLand);

    if (onLand) {
      setLayerValues([]);
      setIsCalculating(false);
      return;
    }

    // Calculate layer values for water
    const visibleLayers = globalState.layers.filter(l => l.visible);

    const values: ILayerValue[] = visibleLayers.map(layer => {
      const def = LayerUtils.layerTypeToDefinition(layer.type);
      const value = IDW.interpolateLayer(lng, lat, layer);

      return {
        layer,
        name: def.name,
        unit: def.unit,
        decimals: def.decimals,
        value
      };
    });

    setLayerValues(values);
    setIsCalculating(false);
  };

  const handleMouseMove = (e: MapMouseEvent) => {
    // Update position immediately for tooltip positioning
    const newLngLat = { lng: e.lngLat.lng, lat: e.lngLat.lat };
    setLngLat(newLngLat);
    lngLatRef.current = newLngLat;

    // Debounce the calculations
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      calculateValues();
      debounceRef.current = null;
    }, DEBOUNCE_MS);
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.code === 'Escape') {
      props.onCancel();
    }
  };

  const mount = () => {
    document.addEventListener('keydown', handleKeydown);
    map.on('mousemove', handleMouseMove);
    map.getCanvas().style.cursor = 'crosshair';
  };

  const unmount = () => {
    document.removeEventListener('keydown', handleKeydown);
    map.off('mousemove', handleMouseMove);
    map.getCanvas().style.cursor = '';
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
  };

  React.useEffect(() => {
    mount();
    return unmount;
  }, []);

  // Recalculate if layers change while eyedropper is active
  React.useEffect(() => {
    if (lngLatRef.current) {
      calculateValues();
    }
  }, [globalState.layers]);

  const formatValue = (lv: ILayerValue): React.ReactNode => {
    if (lv.value === null) {
      return <NoData>No data</NoData>;
    }
    const val = typeof lv.value === 'number' ? lv.value : lv.value[0];
    return <NumberFormatter value={val} decimals={lv.decimals} />;
  };

  const formatName = (lv: ILayerValue): React.ReactNode => {
    if (lv.unit) {
      return <>{lv.name} <Unit>({lv.unit})</Unit></>;
    }
    return lv.name;
  };

  const renderCurrentSpeedRows = (lv: ILayerValue): React.ReactNode => {
    if (lv.value === null) {
      return <InfoBox.Row name={lv.name} value={<NoData>No data</NoData>} />;
    }
    const [u, v] = lv.value as number[];
    return (
      <>
        <InfoBox.Row name={<>U <Unit>({lv.unit})</Unit></>} value={<NumberFormatter value={u} decimals={lv.decimals} />} />
        <InfoBox.Row name={<>V <Unit>({lv.unit})</Unit></>} value={<NumberFormatter value={v} decimals={lv.decimals} />} />
      </>
    );
  };

  if (!lngLat) return null;

  return (
    <Dockable
      longitude={lngLat.lng}
      latitude={lngLat.lat}
      offsetX={16}
      offsetY={16}
      swappable="both"
      hysteresis={30}
    >
      <InfoBox width={280} padded>
        <Coordinates>
          <LongitudeFormatter value={lngLat.lng} />, <LatitudeFormatter value={lngLat.lat} />
        </Coordinates>
        {isCalculating ? (
          <Message>Calculating...</Message>
        ) : isLand ? (
          <Message>Land</Message>
        ) : layerValues.length === 0 ? (
          <Message>No visible layers</Message>
        ) : (
          layerValues.map((lv, idx) => (
            lv.layer.type === ELayerType.CurrentSpeed
              ? <React.Fragment key={idx}>{renderCurrentSpeedRows(lv)}</React.Fragment>
              : <InfoBox.Row key={idx} name={formatName(lv)} value={formatValue(lv)} />
          ))
        )}
      </InfoBox>
    </Dockable>
  );
};

const Coordinates = styled.div`
  color: #fff;
  font-size: 11px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Unit = styled.span`
  font-size: 80%;
  font-weight: normal;
  color: #dadada;
`;

const Message = styled.div`
  color: #fff;
  font-style: italic;
  text-align: center;
  padding: 8px 0;
`;

const NoData = styled.span`
  color: #aaa;
  font-style: italic;
`;

export { Eyedropper };
